import { UserInfo } from '../../../../shared/models/auth/user-info.model';
import { TokenStorageService } from '@app/shared/services/token-storage.service';
import { AuthService } from '@app/api/api-services/auth.service';
import { RouteHelperService } from '@app/shared/services/route-helper.service';
import { Injectable } from '@angular/core';
import { ResponsiveService } from 'src/app/shared/services';
import { LayoutService } from 'src/app/shared/services/layout.service';
import { CustomUtils } from 'src/app/shared/utils/custom-utils';
import { SidebarMenuItem } from 'src/app/modules/common-features/sidebar/models/sidebar-menu-item.model';
import { SidebarMenuContainer } from 'src/app/modules/common-features/sidebar/models/sidebar-menu-container.model';
import { ProfileService } from '@app/api/api-services/profile.service';

@Injectable()
export abstract class ASidebarBuilder {
  protected nightModeMenuItem: SidebarMenuItem;
  protected nightModeInactiveIcon = 'moon';
  protected nightModeInactiveText = 'Night Mode';
  protected nightModeActiveIcon = 'sun';
  protected nightModeActiveText = 'Day Mode';

  public programSelectorContent: Array<SidebarMenuItem>;
  public programMenuContent: Array<SidebarMenuItem | SidebarMenuContainer>;
  public systemMenuContent: Array<SidebarMenuItem | SidebarMenuContainer>;
  public utilsMenuContent: Array<SidebarMenuItem | SidebarMenuContainer>;

  public constructor(
    protected readonly tokenStorageService: TokenStorageService,
    protected readonly routeHelper: RouteHelperService,
    protected readonly responsiveService: ResponsiveService,
    protected readonly authService: AuthService,
    protected readonly layoutService: LayoutService,
    protected readonly profileService: ProfileService
  ) {
    this.profileService.themePreferenceChanged$.subscribe((isNightMode: boolean) => {
      if (!this.tokenStorageService.userInfo?.CurrentSite.PlatformTopNavEnabled) {
        this.setNightModeMenuItem(isNightMode);
      }
    });
  }

  public rebuild(userInfo: UserInfo): void {
    this.rebuildProgramSelector(userInfo);
    this.rebuildProgramMenu(userInfo);
    this.rebuildSystemMenu(userInfo);
    if (!userInfo.CurrentSite.PlatformTopNavEnabled) {
      this.rebuildUtilsMenu();
    }
  }

  protected abstract rebuildProgramMenu(userInfo: UserInfo): void;

  protected abstract rebuildProgramSettingsMenu(
    userInfo: UserInfo,
    content: Array<SidebarMenuItem | SidebarMenuContainer>
  ): void;

  protected rebuildProgramSelector(userInfo: UserInfo): void {
    this.programSelectorContent = userInfo.AvailablePrograms.map(
      (x) =>
        new SidebarMenuItem({
          name: x.Name,
          label: CustomUtils.getProgramType(x.Type),
          action: () => {
            this.routeHelper.changeCurrent(this.tokenStorageService.userInfo.CurrentSiteId, x.Id);
          }
        })
    );
  }

  protected rebuildSystemMenu(userInfo: UserInfo): void {
    const content = new Array<SidebarMenuItem | SidebarMenuContainer>();

    if (userInfo.CurrentProgramId !== null) {
      this.rebuildProgramSettingsMenu(userInfo, content);
    }

    if (
      !userInfo.CurrentSite.PlatformTopNavEnabled &&
      (userInfo.IsSiteAdmin || (userInfo.IsSysAdmin && userInfo.CurrentSiteId))
    ) {
      const systemSubMenu = new SidebarMenuContainer({
        icon: 'gear',
        name: 'Site Settings'
      });

      systemSubMenu.sublings.push(
        new SidebarMenuItem({
          name: 'Dashboard',
          isActive: this.routeHelper.siteDashboardActive.bind(this.routeHelper),
          action: this.routeHelper.siteDashboard.bind(this.routeHelper)
        })
      );

      systemSubMenu.sublings.push(
        new SidebarMenuItem({
          name: 'User Management',
          isActive: this.routeHelper.usersActive.bind(this.routeHelper),
          action: this.routeHelper.users.bind(this.routeHelper)
        })
      );

      systemSubMenu.sublings.push(
        new SidebarMenuItem({
          name: 'Programs',
          isActive: this.routeHelper.programsActive.bind(this.routeHelper),
          action: this.routeHelper.programs.bind(this.routeHelper)
        })
      );

      systemSubMenu.sublings.push(
        new SidebarMenuItem({
          name: 'Site Lists',
          isActive: this.routeHelper.siteListsActive.bind(this.routeHelper),
          action: this.routeHelper.siteLists.bind(this.routeHelper)
        })
      );

      systemSubMenu.sublings.push(
        new SidebarMenuItem({
          name: 'Add-Ons',
          isActive: this.routeHelper.addOnsActive.bind(this.routeHelper),
          action: this.routeHelper.addOns.bind(this.routeHelper)
        })
      );

      systemSubMenu.sublings.push(
        new SidebarMenuItem({
          name: 'Agency Information',
          isActive: this.routeHelper.agencyInformationActive.bind(this.routeHelper),
          action: this.routeHelper.agencyInformation.bind(this.routeHelper)
        })
      );

      systemSubMenu.sublings.push(
        new SidebarMenuItem({
          name: 'Audit Log',
          isActive: this.routeHelper.auditLogActive.bind(this.routeHelper),
          action: this.routeHelper.auditLog.bind(this.routeHelper)
        })
      );

      content.push(systemSubMenu);
    }

    if (userInfo.IsSysAdmin && !userInfo.CurrentSite.PlatformTopNavEnabled) {
      const superUserSubmenu = new SidebarMenuContainer({
        icon: 'gears',
        name: 'System Settings'
      });

      superUserSubmenu.sublings.push(
        new SidebarMenuItem({
          name: 'Dashboard',
          isActive: this.routeHelper.systemDashboardActive.bind(this.routeHelper),
          action: this.routeHelper.systemDashboard.bind(this.routeHelper)
        })
      );

      superUserSubmenu.sublings.push(
        new SidebarMenuItem({
          name: 'Sites',
          isActive: this.routeHelper.sitesActive.bind(this.routeHelper),
          action: this.routeHelper.sites.bind(this.routeHelper)
        })
      );

      superUserSubmenu.sublings.push(
        new SidebarMenuItem({
          name: 'Templates',
          isActive: this.routeHelper.templatesActive.bind(this.routeHelper),
          action: this.routeHelper.templates.bind(this.routeHelper)
        })
      );
      superUserSubmenu.sublings.push(
        new SidebarMenuItem({
          name: 'Super User',
          isActive: this.routeHelper.superUsersActive.bind(this.routeHelper),
          action: this.routeHelper.superUsers.bind(this.routeHelper)
        })
      );
      superUserSubmenu.sublings.push(
        new SidebarMenuItem({
          name: 'Background Services',
          isActive: this.routeHelper.backgroundServicesActive.bind(this.routeHelper),
          action: this.routeHelper.backgroundServices.bind(this.routeHelper)
        })
      );

      content.push(superUserSubmenu);
    }

    if (userInfo.IsSysAdmin) {
      const aiSubMenu = new SidebarMenuContainer({
        icon: 'sparkles',
        name: 'AI Settings'
      });
      aiSubMenu.sublings.push(
        new SidebarMenuItem({
          name: 'Dashboard',
          isActive: this.routeHelper.aiDashboardActive.bind(this.routeHelper),
          action: this.routeHelper.aiDashboard.bind(this.routeHelper)
        })
      );
      aiSubMenu.sublings.push(
        new SidebarMenuItem({
          name: 'Prompt Management',
          isActive: this.routeHelper.aiPromptsActive.bind(this.routeHelper),
          action: this.routeHelper.aiPrompts.bind(this.routeHelper)
        })
      );
      content.push(aiSubMenu);
    }

    this.systemMenuContent = content;
  }

  protected rebuildUtilsMenu(): void {
    const content = new Array<SidebarMenuItem | SidebarMenuContainer>();

    if (this.nightModeMenuItem && this.nightModeMenuItem.name === this.nightModeActiveText) {
      this.nightModeMenuItem = new SidebarMenuItem({
        name: this.nightModeActiveText,
        icon: this.nightModeActiveIcon,
        action: this.toggleNightMode.bind(this),
        disableCollapse: true
      });
    } else {
      this.nightModeMenuItem = new SidebarMenuItem({
        name: this.nightModeInactiveText,
        icon: this.nightModeInactiveIcon,
        action: this.toggleNightMode.bind(this),
        disableCollapse: true
      });
    }

    content.push(this.nightModeMenuItem);

    const helpSubMenu = new SidebarMenuContainer({
      icon: 'filled-question',
      name: 'Help & Support'
    });

    helpSubMenu.sublings.push(
      new SidebarMenuItem({
        name: 'Help Center',
        action: () => window.open('https://success.neogov.com/', '_blank', 'noopener')
      })
    );

    helpSubMenu.sublings.push(
      new SidebarMenuItem({
        name: 'Product Training',
        action: () => window.open('https://www.powerdmsuniversity.com/', '_blank', 'noopener')
      })
    );

    content.push(helpSubMenu);

    content.push(
      new SidebarMenuItem({
        icon: 'logout',
        name: 'Logout',
        action: () => this.routeHelper.logout()
      })
    );

    this.utilsMenuContent = content;
  }

  private setNightModeMenuItem(isNightMode: boolean): void {
    if (isNightMode) {
      this.nightModeMenuItem.icon = this.nightModeActiveIcon;
      this.nightModeMenuItem.name = this.nightModeActiveText;
    } else {
      this.nightModeMenuItem.icon = this.nightModeInactiveIcon;
      this.nightModeMenuItem.name = this.nightModeInactiveText;
    }
  }

  protected toggleNightMode(): void {
    if (this.nightModeMenuItem.icon === this.nightModeInactiveIcon) {
      this.nightModeMenuItem.icon = this.nightModeActiveIcon;
      this.nightModeMenuItem.name = this.nightModeActiveText;
      this.layoutService.enableNightMode(true);
    } else {
      this.nightModeMenuItem.icon = this.nightModeInactiveIcon;
      this.nightModeMenuItem.name = this.nightModeInactiveText;
      this.layoutService.enableDayMode(true);
    }
  }

  public cleanup(): void {
    this.programSelectorContent = [];
    this.programMenuContent = [];
    this.systemMenuContent = [];
    this.utilsMenuContent = [];
    this.nightModeMenuItem = null;
  }
}
